import React from 'react';
import { Tag } from 'antd';
import { JobSource } from '../../types';

// Only the FIRST-PARTY boards have a label of their own. `google_jobs` is an
// aggregator: the board the ad actually appeared on arrives as `sourceSite`,
// and when the API could not name it there is nothing truthful to say except
// "we don't know" — so no tag is rendered at all (see `labelFor`). Naming
// Google is the one thing the owner ruled out for every user-facing surface.
const LABELS: Record<Exclude<JobSource, 'google_jobs'>, string> = {
  linkedin: 'via LinkedIn',
  indeed: 'via Indeed',
};

/**
 * Turns whatever the API put in `source` / `sourceSite` into a label a user can
 * trust, or `null` when there is no truthful label to show.
 *
 * Deliberately defensive about the input, because the contract is weaker than
 * the type suggests. `source` is a plain string with a default on the API's
 * DTOs, so it lands OUTSIDE the OpenAPI `required` set (absent/null is
 * possible), and beast-api's `validate_source`
 * (src/app/components/jobs/dtos.py)
 * passes ANY non-empty string through rather than 500ing — "every persisted
 * source is in SOURCES" is enforced nowhere at the write boundary: no CHECK
 * constraint, no validator. So a board this app has never heard of can reach
 * here, and a user's whole board must not fail to load over one odd row.
 *
 * If `validate_source` ever grows a SOURCES membership check, the unknown-board
 * arm here becomes unreachable — leave it, it costs nothing and the two halves
 * of the contract are meant to reference each other.
 */
const labelFor = (source?: JobSource | string | null, sourceSite?: string | null): string | null => {
  // The ORIGIN board wins when the crawler named it. It is already a display
  // string ("Stepstone", "Bundesagentur fuer Arbeit"), so it is rendered AS-IS:
  // no lowercasing, no capitalising, no lookup — any of those would mangle a
  // real board's name. The API normalises it before it reaches the wire
  // (beast-api `normalize_board_name`: one line, no control characters, at
  // most 80 characters, else null), which is why there is no cap here — a cap
  // would only mangle a legitimate name for a layout risk the API already
  // bounds. Trimmed, so a padded value never renders as "via " and nothing;
  // blank falls through to `source` exactly like null does.
  const site = typeof sourceSite === 'string' ? sourceSite.trim() : '';
  if (site !== '') return `via ${site}`;
  // Absent, null, or blank means an older backend, which is LinkedIn-only.
  const key = (source ?? '').trim().toLowerCase();
  if (key === '') return LABELS.linkedin;
  if (key === 'linkedin' || key === 'indeed') return LABELS[key];
  // An aggregator whose origin the API could not name: there is no truthful
  // board to show, and "via Google Jobs" is the one label ruled out, so show
  // nothing rather than something misleading.
  if (key === 'google_jobs') return null;
  // An unknown board: name it rather than mislabel it as LinkedIn. Never blank,
  // never "undefined".
  return `via ${key.charAt(0).toUpperCase()}${key.slice(1)}`;
};

/**
 * "via Stepstone" when the API names the board the ad originally appeared on,
 * else "via Indeed" / "via LinkedIn" from `source`. A missing source is an
 * older backend, i.e. LinkedIn. Renders NOTHING for a Google Jobs ad whose
 * origin is unknown — never "via Google Jobs".
 */
const SourceTag: React.FC<{
  source?: JobSource | string | null;
  sourceSite?: string | null;
  className?: string;
}> = ({ source, sourceSite, className }) => {
  const label = labelFor(source, sourceSite);
  if (label === null) return null;
  return (
    <Tag className={className} data-testid="source-tag">
      {label}
    </Tag>
  );
};

export default SourceTag;
